/* eslint-disable no-useless-escape */
import React, { useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTableFetching } from 'hooks'
import { routes } from 'routes'
import { styled, s } from '@vega/styled/v2'
import { DottedCircleLoader, Menu } from '@vega/components'
import { Table } from '@vega/components/src/v2'
import { Columns, Rows } from './TableFormat'
import FiltersBar from './FiltersBar'
import { searchOptions } from './filterOptions'
import { Item } from '@react-stately/collections'
import { ReactComponent as Search } from '@vega/components/src/assets/images/search.svg'

import {
  searchLegalEntities,
  selectAllLegalEntities,
  selectTotal,
} from 'modules/legalEntity'

const Root = styled.div(s('flex flex-column flex-1 mx-8'))
const Row = styled.div(s('flex flex-row pb-4 pt-8'))
const Title = styled.h3(s('p-0 m-0 text-primary font-semibold', { fontSize: 28 }))

const SearchAndFiltersContainer = styled.div(
  s('flex my-4 justify-between', { gap: 16 })
)

const Container = styled.div(s('flex justify-between w-full h-full', { gap: 10 }))

const SearchFilter = styled(Menu)(
  s('', {
    minWidth: '195px',
    maxWidth: '195px',
  })
)

const SearchBarContainer = styled.div(
  s('flex items-center w-full h-full rounded-lg rounded-tr-none bg-green-50', {
    minHeight: '52px',
    whiteSpace: 'nowrap',
    outline: 'none',
  })
)
const SearchIcon = styled(Search)(s('absolute ml-4'))

const Input = styled.input(
  s(
    'pl-12 pr-4 pt-3 pb-3 w-full h-full border-solid border-1 border-grey-400 text-base text-grey-500 rounded-lg',
    {
      backgroundcolor: 'white',
      '&:hover': s('border-1 border-primary', { cursor: 'pointer' }),
      ':focus-within': s('border-1 border-primary', {
        outline: '2px solid',
        outlineColor: s('text-primary').color,
      }),
    }
  )
)

const TableContainer = styled.div(s('h-full mt-4', { overflow: 'hidden' }))

const Overview = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [hasFirstFetchFulfilled, setHasFirstFetchFulfilled] = useState(false)
  const total = useSelector(selectTotal)
  const entities = useSelector(selectAllLegalEntities)
  const [searchFilter, setSearchFilter] = useState({
    key: 'name',
    label: 'Entity Name',
    searchLabel: 'Search entity name',
  })

  const columns = Columns()
  const data = Rows({ entities })

  const handleRowClick = (id) => {
    history.push(`${routes.admin.customers.root}/${id}`)
  }

  const fetchData = useCallback(
    ({ searchParams = {}, pageIndex = 0 }) =>
      dispatch(searchLegalEntities({ searchParams, pageIndex })),
    [dispatch]
  )

  const {
    searchProps: { searchParams },
    updateSearchProps,
    paginationProps,
    isFetchingData,
  } = useTableFetching(total, fetchData)

  React.useEffect(() => {
    async function fetchLegalEntitiesOnMount() {
      await fetchData({
        searchParams: { limit: searchParams.limit },
        pageIndex: 0,
      })
      setHasFirstFetchFulfilled(true)
    }

    fetchLegalEntitiesOnMount()
  }, [dispatch, fetchData, searchParams.limit])

  const onTextSearch = ({ searchTerm }, filters) => {
    updateSearchProps({
      searchTerm,
      filters,
    })
  }

  if (!hasFirstFetchFulfilled) {
    return (
      <div style={s('flex justify-center items-center h-full w-full')}>
        <DottedCircleLoader size="lg" />
      </div>
    )
  }

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value

    // regex to validate search text input - not allowing some special characters

    if (/[!"`'#%&,:;<>={}~$()*/\\?[\]^|]+/i.test(searchTerm) === true) {
      document.getElementById('searchTerm').style.outlineColor = 'red'
      return
    }
    document.getElementById('searchTerm').style.outlineColor = '#490B2D'

    if (!searchTerm || searchTerm.length === 0)
      onTextSearch({ searchTerm: '' }, { searchFilter: searchFilter.key })
    if (searchTerm.length > 2)
      onTextSearch({ searchTerm }, { searchFilter: searchFilter.key })
  }

  const handleSearchFilterAction = (searchFilterValue) => {
    if (!searchFilterValue || searchFilterValue.length === 0) searchFilterValue = 'name'
    const updateFilterObject = searchOptions.find(
      (filter) => filter.key === searchFilterValue
    )
    setSearchFilter(updateFilterObject)
  }
  return (
    <Root>
      <Row style={s('items-center justify-between')}>
        <Title className="sohne" style={s('items-center')}>
          Entity Management
        </Title>
      </Row>

      <SearchAndFiltersContainer>
        <Container>
          <SearchFilter
            label={searchFilter.label || 'Entity Name'}
            aria-label="search filter"
            onAction={handleSearchFilterAction}
          >
            {searchOptions.map((option) => (
              <Item key={option.key} value={option.key}>
                {option.label}
              </Item>
            ))}
          </SearchFilter>
          <SearchBarContainer>
            <SearchIcon height={20} width={20} />
            <Input
              placeholder={searchFilter.searchLabel || 'Search entity name'}
              onChange={handleSearchChange}
              name="searchTerm"
              id="searchTerm"
              type="search"
            />
          </SearchBarContainer>
        </Container>

        <FiltersBar
          updateSearchProps={updateSearchProps}
          filters={searchParams.filters}
          setSearchFilter={setSearchFilter}
        />
      </SearchAndFiltersContainer>

      <TableContainer>
        <Table
          columns={columns}
          data={data}
          fetchData={fetchData}
          tBodyStyle={s('bg-white')}
          containerStyle={s('bg-white rounded-lg shadow-md flex-1')}
          paginationProps={paginationProps}
          style={s('pb-0 m-0')}
          tableHeaderRowStyles={s(
            'bg-white border-solid border-0 border-b-1 border-grey-200'
          )}
          loaderLabel="Fetching customers"
          loading={isFetchingData}
          onClick={handleRowClick}
        />
      </TableContainer>
    </Root>
  )
}

export default Overview
